import React, { useEffect, useRef, useState } from 'react';
import { Video, RefreshCw } from 'lucide-react';
import Card from '../ui/Card';
import Button from '../ui/Button';
import Alert from '../ui/Alert';
import { eventProcessingService, processingError, EventVideoJob } from '../../services/event-processing.service';

const ACTIVE = new Set(['pending', 'collecting', 'waiting', 'publishing', 'published', 'processing']);
const SNAPSHOT_ACTIVE = new Set(['pending', 'waiting', 'processing']);
const LABELS: Record<EventVideoJob['video_status'], string> = {
  pending: 'Preparing recording', collecting: 'Recording motion in progress', waiting: 'Waiting for recording / retry', publishing: 'Sending recording job', published: 'Queued for download',
  processing: 'Downloading and preparing playback', completed: 'Recording ready', failed: 'Recording failed',
  skipped: 'Automatic recording unavailable', not_scheduled: 'No recording job scheduled',
};

const EventVideoProcessing: React.FC<{ eventId: number; onReady: () => void }> = ({ eventId, onReady }) => {
  const [job, setJob] = useState<EventVideoJob | null>(null);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [revision, setRevision] = useState(0);
  const ready = useRef(onReady); ready.current = onReady;

  useEffect(() => {
    let active = true;
    let timer: ReturnType<typeof setTimeout>;
    let previous = '';
    let previousMedia = '';
    const refresh = async () => {
      try {
        const result = await eventProcessingService.getVideoJob(eventId);
        if (!active) return;
        setJob(result); setError('');
        const media = JSON.stringify([result.snapshot_url, result.thumbnail_url, result.video_url]);
        const newMedia = !!(result.snapshot_url || result.thumbnail_url || result.video_url) && media !== previousMedia;
        if (newMedia || (result.video_status === 'completed' && ACTIVE.has(previous))) ready.current();
        previousMedia = media;
        previous = result.video_status;
        const snapshotPending = SNAPSHOT_ACTIVE.has(result.snapshot_status || '');
        if (snapshotPending || ACTIVE.has(result.video_status)) timer = setTimeout(refresh, snapshotPending ? 2000 : 10000);
      } catch (err) { if (active) setError(processingError(err)); }
    };
    void refresh();
    return () => { active = false; clearTimeout(timer); };
  }, [eventId, revision]);

  const queue = async () => {
    setBusy(true); setError('');
    try {
      if (job?.video_status === 'failed') await eventProcessingService.retryVideoJob(eventId);
      else await eventProcessingService.queueVideoJob(eventId);
      setRevision(value => value + 1);
    } catch (err) { setError(processingError(err)); }
    finally { setBusy(false); }
  };

  return <Card padding="lg">
    <div className="flex flex-wrap items-start justify-between gap-4">
      <div className="flex gap-3"><Video size={22} className="text-accent-primary mt-1" /><div>
        <h2 className="text-lg font-semibold text-text-primary">Event recording</h2>
        <p role="status" className="text-sm text-text-muted mt-1">{job ? LABELS[job.video_status] : 'Checking recording status…'}</p>
        {job?.snapshot_status && job.snapshot_status !== 'not_scheduled' && <p className="text-sm text-text-secondary mt-1">
          {job.snapshot_status === 'completed' ? 'Screenshot ready — video is prepared separately.'
            : job.snapshot_url ? (SNAPSHOT_ACTIVE.has(job.snapshot_status) ? 'Screenshot available. Saving a copy…' : 'Screenshot available.')
              : SNAPSHOT_ACTIVE.has(job.snapshot_status) ? 'Preparing event screenshot…'
              : 'Event screenshot unavailable. Recording processing continues independently.'}
        </p>}
        {!!job?.attempts && <p className="text-xs text-text-muted mt-1">Download attempts: {job.attempts} of 4</p>}
      </div></div>
      <div className="flex flex-wrap gap-2">
        <Button type="button" size="sm" variant="ghost" leftIcon={<RefreshCw size={14} />} onClick={() => setRevision(value => value + 1)}>Refresh status</Button>
        {job && ['failed', 'skipped', 'not_scheduled'].includes(job.video_status) && <Button type="button" size="sm" isLoading={busy} onClick={queue}>{job.video_status === 'failed' ? 'Retry recording' : 'Queue recording'}</Button>}
      </div>
    </div>
    {error && <div className="mt-3"><Alert variant="error" message={error} /></div>}
    {job?.last_error && <p className="text-xs text-text-muted mt-3">{job.last_error}</p>}
    {job?.video_status === 'skipped' && <p className="text-xs text-text-muted mt-3">Automatic recordings require company S3 event uploads and external NVR access for the location.</p>}
  </Card>;
};
export default EventVideoProcessing;
